import { Tooltip as ChakraTooltip, Portal, chakra } from '@chakra-ui/react';
import * as React from 'react';

export interface TooltipProps extends ChakraTooltip.RootProps {
  showArrow?: boolean;
  portalled?: boolean;
  portalRef?: React.RefObject<HTMLElement | null>;
  content: React.ReactNode;
  contentProps?: ChakraTooltip.ContentProps;
  disabled?: boolean;
  /** Wrap even a single element child in the trigger `span` (v2 prop). */
  shouldWrapChildren?: boolean;
}

/*
 * `Tooltip.Trigger` is `asChild`, and Ark's `asChild` renders nothing at all
 * for a child that isn't a single element — so a text label like
 * `<Tooltip>Renew</Tooltip>` would vanish. v2's Tooltip wrapped any
 * non-element children (and every child under `shouldWrapChildren`) in a
 * focusable inline-block `span`; this restores exactly that. Fragments are
 * wrapped too: they're valid elements, but can't carry the trigger's props.
 */
const needsTriggerWrapper = (children: React.ReactNode) =>
  !React.isValidElement(children) || children.type === React.Fragment;

export const Tooltip = React.forwardRef<HTMLDivElement, TooltipProps>(
  function Tooltip(props, ref) {
    const {
      showArrow,
      children,
      disabled,
      portalled = true,
      content,
      contentProps,
      portalRef,
      shouldWrapChildren,
      ...rest
    } = props;

    const trigger =
      shouldWrapChildren || needsTriggerWrapper(children) ? (
        <chakra.span display="inline-block" tabIndex={0}>
          {children}
        </chakra.span>
      ) : (
        children
      );

    if (disabled) {
      return trigger;
    }

    return (
      <ChakraTooltip.Root {...rest}>
        <ChakraTooltip.Trigger asChild>{trigger}</ChakraTooltip.Trigger>
        <Portal disabled={!portalled} container={portalRef}>
          <ChakraTooltip.Positioner>
            <ChakraTooltip.Content ref={ref} {...contentProps}>
              {showArrow && (
                <ChakraTooltip.Arrow>
                  <ChakraTooltip.ArrowTip />
                </ChakraTooltip.Arrow>
              )}
              {content}
            </ChakraTooltip.Content>
          </ChakraTooltip.Positioner>
        </Portal>
      </ChakraTooltip.Root>
    );
  },
);
